import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { 
  Plus, 
  FileText, 
  GripVertical, 
  Trash2, 
  Settings, 
  Eye, 
  Link2, 
  Copy, 
  CopyPlus,
  ExternalLink,
  Loader2,
  Edit,
  Globe,
  Archive,
  Users,
  Workflow,
  Shuffle,
  Scale,
  Hash,
  Hand,
  Sigma,
} from 'lucide-react';
import { toast } from 'sonner';

// Available field types for drag and drop
const AVAILABLE_FIELDS = [
  // Basic fields
  { type: 'text', label: 'Text Input', icon: '📝', applicationField: null },
  { type: 'email', label: 'Email', icon: '✉️', applicationField: 'student_email' },
  { type: 'phone', label: 'Phone', icon: '📱', applicationField: 'student_phone' },
  { type: 'phone_intl', label: 'Phone (Intl)', icon: '🌍', applicationField: 'student_phone', description: 'Phone with country code' },
  { type: 'date', label: 'Date', icon: '📅', applicationField: null },
  { type: 'select', label: 'Dropdown', icon: '📋', applicationField: null },
  { type: 'textarea', label: 'Text Area', icon: '📄', applicationField: null },
  { type: 'file', label: 'File Upload', icon: '📎', applicationField: null },
  { type: 'checkbox', label: 'Checkbox', icon: '☑️', applicationField: null },
  // Application-specific fields
  { type: 'university_select', label: 'University', icon: '🏛️', applicationField: 'university_id', isAppField: true, description: 'Select university' },
  { type: 'nationality_select', label: 'Nationality', icon: '🌍', applicationField: 'nationality', isAppField: true, description: 'Student nationality (for document requirements)' },
  { type: 'country_select', label: 'Country Living In', icon: '🏠', applicationField: 'country_living_in', isAppField: true, description: 'Current country of residence' },
  { type: 'program_select', label: 'Program', icon: '🎓', applicationField: 'program_id', isAppField: true },
  { type: 'degree_type_select', label: 'Degree Type', icon: '🎯', applicationField: 'degree_type', isAppField: true, description: 'Bachelor, Master, PhD, etc.' },
  { type: 'document_upload', label: 'Document Upload', icon: '📄', applicationField: null, isAppField: true, description: 'Upload with document type selection' },
  { type: 'documents_list', label: 'Documents List', icon: '📑', applicationField: null, isAppField: true, description: 'Multiple document attachments' },
  { type: 'conditional_documents', label: 'Smart Documents', icon: '🔄', applicationField: null, isAppField: true, description: 'Dynamic docs based on Nationality + Degree' },
];

// Application-specific fields that can be mapped - Counselor + Admission Officer tabs
const APPLICATION_FIELDS = [
  // Basic Info
  { value: 'first_name', label: 'First Name', category: 'Basic' },
  { value: 'last_name', label: 'Last Name', category: 'Basic' },
  { value: 'full_name', label: 'Full Name', category: 'Basic' },
  { value: 'student_email', label: 'Email', category: 'Basic' },
  { value: 'student_phone', label: 'Phone', category: 'Basic' },
  
  // Counselor Tab Fields
  { value: 'date_of_birth', label: 'Date of Birth', category: 'Counselor' },
  { value: 'nationality', label: 'Nationality', category: 'Counselor' },
  { value: 'country_living_in', label: 'Country Living In', category: 'Counselor' },
  { value: 'passport_number', label: 'Passport Number', category: 'Counselor' },
  { value: 'passport_expiry', label: 'Passport Expiry Date', category: 'Counselor' },
  { value: 'gender', label: 'Gender', category: 'Counselor' },
  { value: 'marital_status', label: 'Marital Status', category: 'Counselor' },
  { value: 'address', label: 'Address', category: 'Counselor' },
  { value: 'city', label: 'City', category: 'Counselor' },
  { value: 'state', label: 'State/Province', category: 'Counselor' },
  { value: 'country', label: 'Country', category: 'Counselor' },
  { value: 'postal_code', label: 'Postal Code', category: 'Counselor' },
  { value: 'emergency_contact_name', label: 'Emergency Contact Name', category: 'Counselor' },
  { value: 'emergency_contact_phone', label: 'Emergency Contact Phone', category: 'Counselor' },
  { value: 'emergency_contact_relation', label: 'Emergency Contact Relation', category: 'Counselor' },
  
  // Admission Officer Tab Fields  
  { value: 'university_id', label: 'University', category: 'Admission', isLookup: true },
  { value: 'program_id', label: 'Program', category: 'Admission', isLookup: true },
  { value: 'intake_id', label: 'Intake', category: 'Admission', isLookup: true },
  { value: 'education_level', label: 'Education Level', category: 'Admission' },
  { value: 'previous_degree', label: 'Previous Degree', category: 'Admission' },
  { value: 'previous_institution', label: 'Previous Institution', category: 'Admission' },
  { value: 'graduation_year', label: 'Graduation Year', category: 'Admission' },
  { value: 'gpa', label: 'GPA/Percentage', category: 'Admission' },
  { value: 'english_test_type', label: 'English Test Type (IELTS/TOEFL)', category: 'Admission' },
  { value: 'english_test_score', label: 'English Test Score', category: 'Admission' },
  { value: 'work_experience_years', label: 'Work Experience (Years)', category: 'Admission' },
  { value: 'work_experience_details', label: 'Work Experience Details', category: 'Admission' },
  { value: 'statement_of_purpose', label: 'Statement of Purpose', category: 'Admission' },
  { value: 'additional_notes', label: 'Additional Notes', category: 'Admission' },
];

const FormCreatorPage = () => {
  const { api } = useAuth();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [universities, setUniversities] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [degreeTypes, setDegreeTypes] = useState([]);
  
  // Form builder state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formInternalNotes, setFormInternalNotes] = useState('');
  const [formSlug, setFormSlug] = useState('');
  const [formSlugTouched, setFormSlugTouched] = useState(false);  // once true, stop auto-sync from name
  const [formLogoOverride, setFormLogoOverride] = useState('');   // optional URL to override the university's logo
  const [selectedUniversity, setSelectedUniversity] = useState('');
  const [formFields, setFormFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [draggedField, setDraggedField] = useState(null);
  const [duplicatingFormId, setDuplicatingFormId] = useState(null);

  // Lead Distribution state (embedded in the Form Creator modal)
  const [teams, setTeams] = useState([]);
  const [members, setMembers] = useState([]);            // all tenant users
  const [ldTeamId, setLdTeamId] = useState('');          // required
  const [ldUserIds, setLdUserIds] = useState([]);        // empty = all team members
  const [ldMethod, setLdMethod] = useState('round_robin');
  const [ldWeights, setLdWeights] = useState({});        // { userId: int }
  
  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [formToDelete, setFormToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchForms();
    fetchMasterData();
  }, []);

  useEffect(() => {
    // Fetch programs when university changes
    if (selectedUniversity && selectedUniversity !== 'none') {
      fetchProgramsForUniversity(selectedUniversity);
    }
  }, [selectedUniversity]);

  const fetchForms = async () => {
    setLoading(true);
    try {
      const response = await api().get('/forms');
      setForms(response.data.forms);
    } catch (error) {
      toast.error('Failed to load forms');
    } finally {
      setLoading(false);
    }
  };

  const fetchMasterData = async () => {
    try {
      const [uniRes, progRes, intakeRes, countryRes, docTypeRes, degreeTypeRes, teamsRes, usersRes] = await Promise.all([
        api().get('/master-data?type=university'),
        api().get('/master-data?type=program'),
        api().get('/master-data?type=intake'),
        api().get('/master-data?type=country'),
        api().get('/master-data?type=document_type'),
        api().get('/master-data?type=degree_type'),
        api().get('/teams').catch(() => ({ data: [] })),
        api().get('/users').catch(() => ({ data: [] })),
      ]);
      setUniversities(uniRes.data);
      setPrograms(progRes.data);
      setIntakes(intakeRes.data);
      setCountries(countryRes.data);
      setDocumentTypes(docTypeRes.data);
      setDegreeTypes(degreeTypeRes.data);
      setTeams(teamsRes.data || []);
      const rawUsers = usersRes.data;
      setMembers(Array.isArray(rawUsers) ? rawUsers : (rawUsers?.users || rawUsers?.items || []));
    } catch (error) {
      console.error('Failed to load master data:', error);
    }
  };

  const fetchProgramsForUniversity = async (universityId) => {
    try {
      // Programs have university as parent_id
      const response = await api().get(`/master-data?type=program&parent_id=${universityId}`);
      setPrograms(response.data);
    } catch (error) {
      console.error('Failed to load programs:', error);
    }
  };

  const openCreateForm = () => {
    setFormName('');
    setFormDescription('');
    setFormInternalNotes('');
    setFormSlug('');
    setFormSlugTouched(false);
    setFormLogoOverride('');
    setSelectedUniversity('');
    setFormFields([]);
    setEditingForm(null);
    setLdTeamId('');
    setLdUserIds([]);
    setLdMethod('round_robin');
    setLdWeights({});
    setCreateOpen(true);
  };

  const openEditForm = (form) => {
    setFormName(form.name);
    setFormDescription(form.description || '');
    setFormInternalNotes(form.internal_notes || '');
    setFormSlug(form.unique_slug || '');
    setFormSlugTouched(true);  // editing existing → don't auto-overwrite their slug
    setFormLogoOverride(form.university_logo_url || '');
    setSelectedUniversity(form.university_id || '');
    setFormFields(form.fields || []);
    setEditingForm(form);
    // Hydrate lead-distribution from form payload
    const ld = form.lead_distribution || {};
    setLdTeamId(ld.team_id || '');
    setLdUserIds(Array.isArray(ld.user_ids) ? ld.user_ids : []);
    // Normalise legacy method names to the new UI values
    const legacy = (ld.method || 'round_robin').toLowerCase();
    const methodMap = { load_balanced: 'equal', strict: 'round_robin' };
    setLdMethod(methodMap[legacy] || legacy);
    setLdWeights(ld.weights || {});
    setCreateOpen(true);
    // Fetch programs for the form's university
    if (form.university_id) {
      fetchProgramsForUniversity(form.university_id);
    }
  };

  // Slugify utility: lower-case, [a-z0-9-] only, collapse dashes, max 80 chars
  const slugify = (raw) => (raw || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);

  // Auto-sync slug from name until the user manually edits the slug field
  const handleNameChange = (val) => {
    setFormName(val);
    if (!formSlugTouched) {
      setFormSlug(slugify(val));
    }
  };

  const handleSlugChange = (val) => {
    setFormSlugTouched(true);
    // Light client-side sanitise so the visible value matches what the server will store
    setFormSlug(slugify(val));
  };

  const handleDragStart = (e, fieldType) => {
    setDraggedField(fieldType);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (draggedField) {
      const newField = {
        id: `field_${Date.now()}`,
        type: draggedField.type,
        label: draggedField.label,
        placeholder: '',
        required: false,
        options: getDefaultOptions(draggedField.type),
        applicationField: draggedField.applicationField,
        isAppField: draggedField.isAppField || false
      };
      setFormFields([...formFields, newField]);
    }
    setDraggedField(null);
  };

  // Get default options for select-type fields
  const getDefaultOptions = (fieldType) => {
    switch (fieldType) {
      case 'select':
        return ['Option 1', 'Option 2'];
      case 'country_select':
        // Will be populated from countries master data
        return [];
      case 'program_select':
        // Will be populated from programs master data
        return [];
      case 'degree_type_select':
        // Get degree types from Core Settings, fallback to common types
        return degreeTypes.length > 0 
          ? degreeTypes.map(dt => dt.name) 
          : ['Bachelor', 'Master', 'PhD', 'Diploma', 'Certificate', 'Associate'];
      case 'document_upload':
        // Will be populated from document_types master data
        return [];
      case 'documents_list':
        // Will be populated from document_types master data
        return [];
      default:
        return null;
    }
  };

  const removeField = (fieldId) => {
    setFormFields(formFields.filter(f => f.id !== fieldId));
  };

  const updateField = (fieldId, updates) => {
    setFormFields(formFields.map(f => f.id === fieldId ? { ...f, ...updates } : f));
  };

  const moveField = (fromIndex, toIndex) => {
    const newFields = [...formFields];
    const [removed] = newFields.splice(fromIndex, 1);
    newFields.splice(toIndex, 0, removed);
    setFormFields(newFields);
  };

  // Members that belong to the selected team (active only)
  const teamMembers = ldTeamId
    ? members.filter((m) => m.team_id === ldTeamId && m.status !== 'inactive')
    : [];

  // Effective pool: hand-picked subset OR everyone in the team.
  const effectivePool = ldUserIds.length > 0
    ? teamMembers.filter((m) => ldUserIds.includes(m.id))
    : teamMembers;

  const needsMethodPicker = effectivePool.length > 1;

  const buildLeadDistribution = () => {
    const mode = ldUserIds.length > 0 ? 'specific_members' : 'team_all_members';
    // Only carry weights for the users actually in the pool when weighted
    let weights = undefined;
    if (ldMethod === 'weighted' && needsMethodPicker) {
      weights = {};
      effectivePool.forEach((u) => {
        weights[u.id] = Math.max(parseInt(ldWeights[u.id] || 1, 10) || 1, 1);
      });
    }
    return {
      mode,
      team_id: ldTeamId,
      user_ids: ldUserIds,
      method: needsMethodPicker ? ldMethod : 'equal',
      ...(weights ? { weights } : {}),
    };
  };

  const saveForm = async () => {
    if (!formName.trim()) {
      toast.error('Please enter a form name');
      return;
    }
    if (formFields.length === 0) {
      toast.error('Please add at least one field');
      return;
    }
    // Team selection is required — every form must route leads to a team.
    if (!ldTeamId) {
      toast.error('Please select a team in Lead Distribution');
      return;
    }
    if (teamMembers.length === 0) {
      toast.error('Selected team has no active members. Pick another team or add members in Team Management.');
      return;
    }
    if (ldUserIds.length > 0 && effectivePool.length === 0) {
      toast.error('Your hand-picked members are no longer part of the selected team.');
      return;
    }

    const lead_distribution = buildLeadDistribution();

    setSaving(true);
    try {
      if (editingForm) {
        await api().put(`/forms/${editingForm.id}`, {
          name: formName,
          slug: formSlug || null,
          description: formDescription || null,
          internal_notes: formInternalNotes || null,
          university_logo_url: formLogoOverride || null,
          university_id: selectedUniversity || null,
          fields: formFields,
          lead_distribution,
        });
        toast.success('Form updated successfully');
      } else {
        await api().post('/forms', {
          name: formName,
          slug: formSlug || null,
          description: formDescription || null,
          internal_notes: formInternalNotes || null,
          university_logo_url: formLogoOverride || null,
          university_id: selectedUniversity || null,
          fields: formFields,
          lead_distribution,
        });
        toast.success('Form created successfully');
      }
      setCreateOpen(false);
      fetchForms();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to save form');
    } finally {
      setSaving(false);
    }
  };

  const duplicateForm = async (form) => {
    if (duplicatingFormId) return;
    setDuplicatingFormId(form.id);
    try {
      const r = await api().post(`/forms/${form.id}/duplicate`);
      toast.success(`"${r.data.name}" created as a draft`);
      await fetchForms();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to duplicate form');
    } finally {
      setDuplicatingFormId(null);
    }
  };

  const publishForm = async (formId) => {
    try {
      const response = await api().post(`/forms/${formId}/publish`);
      toast.success('Form published! Link copied to clipboard');
      const publicUrl = `${window.location.origin}${response.data.public_url}`;
      navigator.clipboard.writeText(publicUrl);
      fetchForms();
    } catch (error) {
      toast.error('Failed to publish form');
    }
  };

  const copyFormLink = (form) => {
    const publicUrl = `${window.location.origin}/public/forms/${form.unique_slug}`;
    navigator.clipboard.writeText(publicUrl);
    toast.success('Link copied to clipboard!');
  };

  // Opens the delete confirmation dialog
  const handleDeleteClick = (form) => {
    console.log('Delete button clicked for form:', form.id, form.name);
    setFormToDelete(form);
    setDeleteDialogOpen(true);
  };

  // Actually performs the delete
  const confirmDeleteForm = async () => {
    if (!formToDelete) return;
    
    const formId = formToDelete.id;
    console.log('Confirming delete for form:', formId);
    setDeleting(true);
    
    try {
      console.log('Sending DELETE request to /forms/' + formId);
      const response = await api().delete(`/forms/${formId}`);
      console.log('Delete response:', response.data);
      toast.success('Form deleted');
      setDeleteDialogOpen(false);
      setFormToDelete(null);
      fetchForms();
    } catch (error) {
      console.error('Delete error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to delete form');
    } finally {
      setDeleting(false);
    }
  };

  const renderFieldEditor = () => {
    if (!editingField) return null;
    const field = formFields.find(f => f.id === editingField);
    if (!field) return null;

    // Group application fields by category
    const basicFields = APPLICATION_FIELDS.filter(f => f.category === 'Basic');
    const counselorFields = APPLICATION_FIELDS.filter(f => f.category === 'Counselor');
    const admissionFields = APPLICATION_FIELDS.filter(f => f.category === 'Admission');

    // Check if this is a lookup field mapping that should auto-populate options
    const isLookupField = field.applicationField && 
      ['university_id', 'program_id', 'intake_id'].includes(field.applicationField);

    // Get filtered programs based on selected university
    const getFilteredPrograms = () => {
      if (selectedUniversity && selectedUniversity !== 'none') {
        return programs.filter(p => p.parent_id === selectedUniversity);
      }
      return programs;
    };

    // Get options for lookup fields
    const getLookupOptions = () => {
      if (field.applicationField === 'university_id') {
        return universities.map(u => u.name);
      } else if (field.applicationField === 'program_id') {
        // Filter programs by selected university
        const filteredPrograms = getFilteredPrograms();
        return filteredPrograms.map(p => p.name);
      } else if (field.applicationField === 'intake_id') {
        return intakes.map(i => i.name);
      }
      return [];
    };

    // Auto-update options when mapping changes to a lookup field
    const handleMappingChange = (value) => {
      const newMapping = value === 'none' ? null : value;
      const updates = { applicationField: newMapping };
      
      // Auto-populate options for dropdown fields mapped to lookup fields
      if (field.type === 'select' && newMapping) {
        if (newMapping === 'university_id') {
          updates.options = universities.map(u => u.name);
        } else if (newMapping === 'program_id') {
          // Filter programs by selected university
          const filteredPrograms = getFilteredPrograms();
          updates.options = filteredPrograms.map(p => p.name);
        } else if (newMapping === 'intake_id') {
          updates.options = intakes.map(i => i.name);
        }
      }
      
      updateField(field.id, updates);
    };

    const renderFieldOptions = (fields, groupLabel) => {
      const items = [];
      for (let i = 0; i < fields.length; i++) {
        const f = fields[i];
        items.push(
          <SelectItem key={f.value} value={f.value}>
            {f.label} {f.isLookup && '(Auto-populate)'}
          </SelectItem>
        );
      }
      return items;
    };

    return (
      <Dialog open={!!editingField} onOpenChange={() => setEditingField(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Field: {field.label}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            <div className="space-y-4 py-4 pr-4">
              <div>
                <Label>Label *</Label>
                <Input
                  value={field.label}
                  onChange={(e) => updateField(field.id, { label: e.target.value })}
                />
              </div>
              <div>
                <Label>Placeholder</Label>
                <Input
                  value={field.placeholder || ''}
                  onChange={(e) => updateField(field.id, { placeholder: e.target.value })}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Required</Label>
                <Switch
                  checked={field.required}
                  onCheckedChange={(checked) => updateField(field.id, { required: checked })}
                />
              </div>
              <div>
                <Label>Map to Application Field</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Connect this form field to an application data field
                </p>
                <Select
                  value={field.applicationField || 'none'}
                  onValueChange={handleMappingChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select field mapping" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Mapping</SelectItem>
                    
                    {/* Basic Fields */}
                    <SelectItem value="__basic_header__" disabled className="font-semibold text-muted-foreground">
                      ── Basic Info ──
                    </SelectItem>
                    {renderFieldOptions(basicFields)}
                    
                    {/* Counselor Fields */}
                    <SelectItem value="__counselor_header__" disabled className="font-semibold text-muted-foreground">
                      ── Counselor Tab ──
                    </SelectItem>
                    {renderFieldOptions(counselorFields)}
                    
                    {/* Admission Fields */}
                    <SelectItem value="__admission_header__" disabled className="font-semibold text-muted-foreground">
                      ── Admission Tab ──
                    </SelectItem>
                    {renderFieldOptions(admissionFields)}
                  </SelectContent>
                </Select>
                {isLookupField && field.type === 'select' && (
                  <p className="text-xs text-green-600 mt-1">
                    ✓ Dropdown options will auto-populate from Core Settings
                  </p>
                )}
              </div>
              {field.type === 'select' && (
                <div>
                  <Label>Dropdown Options</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    {isLookupField 
                      ? field.applicationField === 'program_id' && selectedUniversity && selectedUniversity !== 'none'
                        ? `Options filtered by selected university. ${getFilteredPrograms().length} programs available.`
                        : 'Options auto-populated from Core Settings (you can edit if needed)'
                      : 'Enter one option per line'
                    }
                  </p>
                  <Textarea
                    value={(field.options || []).join('\n')}
                    onChange={(e) => updateField(field.id, { options: e.target.value.split('\n').filter(o => o.trim()) })}
                    rows={5}
                  />
                  {isLookupField && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={() => {
                        const newOptions = getLookupOptions();
                        updateField(field.id, { options: newOptions });
                        if (field.applicationField === 'program_id' && selectedUniversity && selectedUniversity !== 'none') {
                          toast.success(`Loaded ${newOptions.length} programs for selected university`);
                        } else {
                          toast.success('Options refreshed from Core Settings');
                        }
                      }}
                    >
                      Refresh from Core Settings
                    </Button>
                  )}
                </div>
              )}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button onClick={() => setEditingField(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  const renderFormBuilder = () => {
    const fieldItems = [];
    for (let i = 0; i < formFields.length; i++) {
      const field = formFields[i];
      fieldItems.push(
        <div
          key={field.id}
          className="flex items-center gap-2 p-3 bg-card border rounded-lg group"
          draggable
          onDragStart={(e) => {
            e.dataTransfer.setData('text/plain', i.toString());
          }}
          onDragOver={(e) => {
            e.preventDefault();
          }}
          onDrop={(e) => {
            e.preventDefault();
            const fromIndex = parseInt(e.dataTransfer.getData('text/plain'));
            if (!isNaN(fromIndex)) {
              moveField(fromIndex, i);
            }
          }}
        >
          <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab" />
          <div className="flex-1">
            <p className="font-medium text-sm">{field.label}</p>
            <p className="text-xs text-muted-foreground">
              {field.type} {field.required && '• Required'}
              {field.applicationField && ` • Maps to: ${field.applicationField}`}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 opacity-0 group-hover:opacity-100"
            onClick={() => setEditingField(field.id)}
          >
            <Settings className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 opacity-0 group-hover:opacity-100 text-destructive hover:bg-destructive/10"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('Remove field clicked:', field.id, field.label);
              removeField(field.id);
            }}
            data-testid={`remove-field-${field.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      );
    }
    return fieldItems;
  };

  const renderAvailableFields = () => {
    // Group fields: basic fields and application-specific fields
    const basicFields = AVAILABLE_FIELDS.filter(f => !f.isAppField);
    const appFields = AVAILABLE_FIELDS.filter(f => f.isAppField);
    
    const renderFieldItem = (field) => (
      <div
        key={field.type}
        className={`flex items-center gap-2 p-2 rounded-lg cursor-grab hover:bg-muted ${field.isAppField ? 'bg-primary/10 border border-primary/20' : 'bg-muted/50'}`}
        draggable
        onDragStart={(e) => handleDragStart(e, field)}
        title={field.description || field.label}
      >
        <span>{field.icon}</span>
        <div className="flex-1 min-w-0">
          <span className="text-sm block">{field.label}</span>
          {field.description && (
            <span className="text-xs text-muted-foreground block truncate">{field.description}</span>
          )}
        </div>
      </div>
    );
    
    return (
      <>
        <p className="text-xs text-muted-foreground mb-2 font-medium">Basic Fields</p>
        <div className="space-y-2 mb-4">
          {basicFields.map(renderFieldItem)}
        </div>
        <p className="text-xs text-muted-foreground mb-2 font-medium">Application Fields</p>
        <div className="space-y-2">
          {appFields.map(renderFieldItem)}
        </div>
      </>
    );
  };

  const renderUniversityOptions = () => {
    const items = [];
    for (let i = 0; i < universities.length; i++) {
      const u = universities[i];
      items.push(
        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
      );
    }
    return items;
  };

  const renderLeadDistributionSection = () => {
    const methodOptions = [
      { v: 'round_robin', label: 'Round Robin',     help: 'Cycle through the pool in order, regardless of workload.', icon: Hash },
      { v: 'equal',       label: 'Equal Split',     help: 'Assign to whoever currently has the fewest open applications.', icon: Scale },
      { v: 'manual',      label: 'Manual Assignment', help: 'Leave incoming leads unassigned — a manager picks them up.', icon: Hand },
      { v: 'weighted',    label: 'Weighted',        help: 'Probability-weighted pick. Set a weight per member below.', icon: Sigma },
    ];

    return (
      <div className="rounded-lg border bg-muted/20 p-4 space-y-4" data-testid="form-lead-distribution-section">
        <div className="flex items-center gap-2">
          <Workflow className="w-4 h-4 text-amber-500" />
          <h4 className="font-medium text-sm">Lead Distribution</h4>
          <Badge variant="outline" className="ml-auto text-[10px]">Required</Badge>
        </div>
        <p className="text-xs text-muted-foreground -mt-2">
          Choose which team receives leads submitted through this form. Optionally hand-pick members
          and a distribution method when more than one person is in the pool.
        </p>

        {/* 1. Team picker */}
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">Team *</Label>
          <Select
            value={ldTeamId || ''}
            onValueChange={(v) => { setLdTeamId(v); setLdUserIds([]); setLdWeights({}); }}
          >
            <SelectTrigger data-testid="ld-team-select">
              <SelectValue placeholder="Select a team…" />
            </SelectTrigger>
            <SelectContent>
              {teams.length === 0 && (
                <div className="px-3 py-2 text-sm text-muted-foreground">
                  No teams found — create one in Team Management first.
                </div>
              )}
              {teams.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {ldTeamId && (
            <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
              <Users className="w-3 h-3" />
              {teamMembers.length} active member{teamMembers.length === 1 ? '' : 's'} in this team
            </p>
          )}
        </div>

        {/* 2. Member multi-select */}
        {ldTeamId && teamMembers.length > 0 && (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold">Members</Label>
              <span className="text-[11px] text-muted-foreground">
                {ldUserIds.length === 0
                  ? `All ${teamMembers.length} members`
                  : `${ldUserIds.length} hand-picked`}
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Leave empty to use all team members. Pick a subset to route leads only to them.
            </p>
            <div className="max-h-40 overflow-y-auto rounded-md border bg-background p-1.5 space-y-0.5">
              {teamMembers.map((u) => {
                const checked = ldUserIds.includes(u.id);
                return (
                  <label
                    key={u.id}
                    className="flex items-center gap-2 px-2 py-1 rounded hover:bg-muted/40 cursor-pointer"
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => {
                        const next = checked
                          ? ldUserIds.filter((x) => x !== u.id)
                          : [...ldUserIds, u.id];
                        setLdUserIds(next);
                      }}
                      data-testid={`ld-user-${u.id}`}
                    />
                    <span className="text-xs flex-1">{u.name || u.email}</span>
                    <span className="text-[10px] text-muted-foreground">{u.role || '—'}</span>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {/* 3. Distribution method (only when pool > 1) */}
        {needsMethodPicker && (
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Distribution Method</Label>
            <p className="text-[11px] text-muted-foreground">
              You picked {effectivePool.length} members — choose how the next lead should be assigned.
            </p>
            <RadioGroup
              value={ldMethod}
              onValueChange={setLdMethod}
              className="grid grid-cols-2 gap-2"
              data-testid="ld-method-group"
            >
              {methodOptions.map((opt) => {
                const Icon = opt.icon;
                const selected = ldMethod === opt.v;
                return (
                  <label
                    key={opt.v}
                    className={`flex items-start gap-2 p-2 rounded-md border cursor-pointer transition-colors ${
                      selected ? 'border-amber-400 bg-amber-50/60 dark:bg-slate-900/30' : 'hover:bg-muted/40'
                    }`}
                    data-testid={`ld-method-${opt.v}`}
                  >
                    <RadioGroupItem value={opt.v} id={`ld-method-${opt.v}`} className="mt-0.5" />
                    <Icon className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                    <div className="text-xs leading-snug">
                      <div className="font-medium">{opt.label}</div>
                      <div className="text-[10px] text-muted-foreground">{opt.help}</div>
                    </div>
                  </label>
                );
              })}
            </RadioGroup>
          </div>
        )}

        {/* 4. Weighted inputs */}
        {needsMethodPicker && ldMethod === 'weighted' && (
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Weights</Label>
            <p className="text-[11px] text-muted-foreground">
              Higher weight → higher chance of getting the next lead. Default is 1.
            </p>
            <div className="space-y-1.5 max-h-32 overflow-y-auto pr-1">
              {effectivePool.map((u) => (
                <div key={u.id} className="flex items-center gap-2">
                  <span className="text-xs flex-1 truncate">{u.name || u.email}</span>
                  <Input
                    type="number"
                    min="1"
                    className="h-7 w-20 text-xs"
                    value={ldWeights[u.id] ?? 1}
                    onChange={(e) => setLdWeights({ ...ldWeights, [u.id]: e.target.value })}
                    data-testid={`ld-weight-${u.id}`}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Summary line */}
        {ldTeamId && effectivePool.length > 0 && (
          <div className="text-[11px] text-muted-foreground border-t pt-2">
            {effectivePool.length === 1
              ? <>All leads will be assigned directly to <strong>{effectivePool[0].name || effectivePool[0].email}</strong>.</>
              : <>Next lead will be routed via <strong>{(methodOptions.find(o => o.v === ldMethod) || {}).label}</strong> across {effectivePool.length} members.</>}
          </div>
        )}
      </div>
    );
  };


  const renderFormsList = () => {
    const items = [];
    for (let i = 0; i < forms.length; i++) {
      const form = forms[i];
      items.push(
        <TableRow key={form.id}>
          <TableCell className="font-medium">{form.name}</TableCell>
          <TableCell>{form.university_name || '-'}</TableCell>
          <TableCell>{form.fields?.length || 0} fields</TableCell>
          <TableCell>
            <Badge variant={form.status === 'published' ? 'default' : 'secondary'}>
              {form.status}
            </Badge>
          </TableCell>
          <TableCell>{form.submissions_count || 0}</TableCell>
          <TableCell>{new Date(form.created_at).toLocaleDateString()}</TableCell>
          <TableCell>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" onClick={() => openEditForm(form)} title="Edit">
                <Edit className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => duplicateForm(form)}
                disabled={duplicatingFormId === form.id}
                title="Duplicate form"
                data-testid={`duplicate-form-${form.id}`}
              >
                {duplicatingFormId === form.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CopyPlus className="w-4 h-4" />}
              </Button>
              {form.status === 'published' ? (
                <>
                  <Button variant="ghost" size="icon" onClick={() => copyFormLink(form)} title="Copy Link">
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => window.open(`/public/forms/${form.unique_slug}`, '_blank')}
                    title="Open Form"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </>
              ) : (
                <Button variant="ghost" size="icon" onClick={() => publishForm(form.id)} title="Publish">
                  <Globe className="w-4 h-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="text-destructive hover:bg-destructive/10"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDeleteClick(form);
                }}
                title="Delete"
                data-testid={`delete-form-${form.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </TableCell>
        </TableRow>
      );
    }
    return items;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="w-6 h-6" />
            Form Creator
          </h1>
          <p className="text-muted-foreground">Design custom forms for university applications</p>
        </div>
        <Button onClick={openCreateForm} data-testid="create-form-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Form
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : forms.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="font-medium mb-2">No forms yet</h3>
              <p className="text-sm text-muted-foreground mb-4">Create your first form to start collecting applications</p>
              <Button onClick={openCreateForm}>
                <Plus className="w-4 h-4 mr-2" />
                Create Form
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>University</TableHead>
                  <TableHead>Fields</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Submissions</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-[150px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>{renderFormsList()}</TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Form Builder Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-5xl max-h-[95vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{editingForm ? 'Edit Form' : 'Create New Form'}</DialogTitle>
            <DialogDescription>
              Drag fields from the left panel to build your form
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto">
            <div className="grid grid-cols-3 gap-4 h-full">
              {/* Left: Available Fields */}
              <div className="space-y-3">
                <h4 className="font-medium text-sm">Available Fields</h4>
                <ScrollArea className="h-[500px]">
                  <div className="space-y-2 pr-4">
                    {renderAvailableFields()}
                  </div>
                </ScrollArea>
              </div>

              {/* Center: Form Canvas */}
              <div className="col-span-2 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Form Name *</Label>
                    <Input
                      value={formName}
                      onChange={(e) => handleNameChange(e.target.value)}
                      placeholder="e.g., Undergraduate Application"
                      data-testid="form-name-input"
                    />
                  </div>
                  <div>
                    <Label>University (Optional)</Label>
                    <Select value={selectedUniversity || "none"} onValueChange={(v) => setSelectedUniversity(v === "none" ? "" : v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select university" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No University</SelectItem>
                        {renderUniversityOptions()}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Public URL slug</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground whitespace-nowrap select-none">
                      {typeof window !== 'undefined' ? window.location.origin : ''}/public/forms/
                    </span>
                    <Input
                      value={formSlug}
                      onChange={(e) => handleSlugChange(e.target.value)}
                      placeholder="sunderland-pakistan"
                      data-testid="form-slug-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-9 px-2 whitespace-nowrap text-xs"
                      onClick={() => { setFormSlugTouched(false); setFormSlug(slugify(formName)); }}
                      disabled={!formName}
                      title="Re-generate slug from form name"
                      data-testid="form-slug-regen-btn"
                    >
                      From name
                    </Button>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Lower-case letters, numbers and dashes only. Must be unique. Re-generate any time from the form name.
                  </p>
                </div>
                <div>
                  <Label>Logo URL <span className="text-muted-foreground font-normal text-xs">(optional override)</span></Label>
                  <Input
                    value={formLogoOverride}
                    onChange={(e) => setFormLogoOverride(e.target.value)}
                    placeholder="https://example.com/university-logo.png"
                    data-testid="form-logo-override-input"
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Leave blank to use the linked university&apos;s logo (configurable in Core Settings → Universities).
                  </p>
                </div>
                <div>
                  <Label>Public description <span className="text-muted-foreground font-normal text-xs">(shown to applicants)</span></Label>
                  <Input
                    value={formDescription}
                    onChange={(e) => setFormDescription(e.target.value)}
                    placeholder="e.g., Apply for September intake — takes 5 minutes."
                    data-testid="form-public-description-input"
                  />
                </div>
                <div>
                  <Label>Internal notes <span className="text-muted-foreground font-normal text-xs">(only your team can see)</span></Label>
                  <textarea
                    value={formInternalNotes}
                    onChange={(e) => setFormInternalNotes(e.target.value)}
                    placeholder="e.g., USK Pakistan Office Team — assign to Karachi office, May 2026 onwards."
                    rows={2}
                    className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    data-testid="form-internal-notes-input"
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Applicants will NEVER see this. Use it to track ownership, intake context, BPO assignment, etc.
                  </p>
                </div>
                
                <div
                  className={`border-2 border-dashed rounded-lg p-4 min-h-[300px] transition-colors ${
                    draggedField ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'
                  }`}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                >
                  {formFields.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                      <FileText className="w-8 h-8 mb-2 opacity-50" />
                      <p className="text-sm">Drag fields here to build your form</p>
                    </div>
                  ) : (
                    <ScrollArea className="h-[280px]">
                      <div className="space-y-2 pr-4">
                        {renderFormBuilder()}
                      </div>
                    </ScrollArea>
                  )}
                </div>

                {/* === Lead Distribution (embedded inside the Form Creator) === */}
                {renderLeadDistributionSection()}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={saveForm} disabled={saving} data-testid="save-form-btn">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {editingForm ? 'Update Form' : 'Create Form'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Field Editor Dialog */}
      {renderFieldEditor()}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Form</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{formToDelete?.name}"? This action cannot be undone.
              {formToDelete?.submissions_count > 0 && (
                <span className="block mt-2 text-destructive font-medium">
                  Warning: This form has {formToDelete.submissions_count} submission(s).
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteForm}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default FormCreatorPage;
